import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LogOut, ChevronDown, Sun, Moon, Menu, Settings, Laptop, MessageSquarePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkspace } from '../../workspace/state/workspaceStore';
import { appPath } from '../../utils/appRouting';
import { useCommunityComposer } from '../../contexts/CommunityComposerContext';
import { SOCIAL_FEATURES_ENABLED } from '../../config/featureFlags';

function headerMetaForPath(pathname, wsMode) {
  const p = String(pathname || '');

  // Keep exact routing logic unchanged; only map to labels.
  if (p.startsWith('/app/caa/agenda-visiva') || p.startsWith('/caa/agenda-visiva')) {
    return { title: 'CAA — Agenda visiva', subtitle: 'Routine visive pronte da stampare' };
  }
  if (p.startsWith('/app/caa') || p.startsWith('/caa')) {
    return { title: 'CAA — Pittogrammi', subtitle: 'Materiali visivi pronti da stampare' };
  }
  if (p.startsWith('/app/ai-didattica') || p.startsWith('/ai-didattica')) {
    return { title: 'AI Didattica', subtitle: 'Genera attività, schede e materiali inclusivi' };
  }
  if (p.startsWith('/app/quiz-live') || p.startsWith('/quiz-live')) {
    return { title: 'Quiz Live', subtitle: 'Quiz inclusivi in tempo reale' };
  }
  if (p.startsWith('/app/bacheca') || p.startsWith('/bacheca')) {
    return { title: 'Bacheca collaborativa', subtitle: 'Crea, condividi e modera in pochi step' };
  }
  if (p.startsWith('/app/b/') || p.startsWith('/b/')) {
    return { title: 'Bacheca', subtitle: 'Accesso e visualizzazione bacheche' };
  }
  if (p.startsWith('/app/libreria-materiali') || p.startsWith('/libreria-materiali')) {
    return { title: 'Libreria materiali', subtitle: 'Raccogli, riapri ed esporta i tuoi materiali' };
  }
  if (p.startsWith('/app/community') || p.startsWith('/community')) {
    return { title: 'Community didattica', subtitle: 'Condivisione e riuso tra docenti (NO marketplace)' };
  }
  if (p.startsWith('/app/calendario') || p.startsWith('/calendario')) {
    return { title: 'Calendario', subtitle: 'Organizza appuntamenti e scadenze' };
  }
  if (p.startsWith('/app/students') || p.startsWith('/students')) {
    if (wsMode === 'class') {
      return { title: 'Le mie classi', subtitle: 'Gestione classi e contesto aula' };
    }
    return { title: 'I miei alunni', subtitle: 'Gestione alunni e profili' };
  }
  if (p.startsWith('/app/pei/verifica') || p.startsWith('/pei/verifica')) {
    return { title: 'Verifica PEI', subtitle: 'Monitoraggio e verifica obiettivi' };
  }
  if (p.startsWith('/app/pei-ministeriale') || p.startsWith('/pei-ministeriale')) {
    return { title: 'PEI Ministeriale', subtitle: 'Compilazione guidata e sezioni' };
  }
  if (p.includes('/verbale-glo')) {
    return { title: 'Verbale GLO', subtitle: 'Riunione e appunti con supporto AI' };
  }
  if (p.includes('/upload')) {
    return { title: 'Carica documento', subtitle: 'Aggiungi documenti e allegati' };
  }
  if (p.startsWith('/app/pdp') || p.startsWith('/pdp')) {
    if (/^\/app\/pdp\//.test(p) || /^\/pdp\//.test(p)) {
      return { title: 'PDP — Compilazione', subtitle: 'Wizard e suggerimenti' };
    }
    return { title: 'PDP — Piano Didattico Personalizzato', subtitle: 'Compilazione guidata, storico e export PDF' };
  }
  if (p.startsWith('/app/diario') || p.startsWith('/diario')) {
    return { title: 'Diario di bordo', subtitle: 'Annotazioni e osservazioni in classe' };
  }
  if (p.startsWith('/app/settings') || p.startsWith('/settings')) {
    return { title: 'Impostazioni', subtitle: 'Profilo, tema e piano' };
  }
  if (p.startsWith('/app/dashboard') || p.startsWith('/dashboard')) {
    return { title: 'Dashboard', subtitle: 'Panoramica rapida del tuo lavoro' };
  }

  return { title: '', subtitle: '' };
}

const Header = ({ onToggleSidebar }) => {
  const { userProfile, logout, displayName, photoURL, initials } = useAuth();
  const { subscription } = useSubscription();
  const { theme, resolvedTheme, setTheme, isDark } = useTheme();
  const ws = useWorkspace();
  const { openComposer } = useCommunityComposer();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const dropdownRef = useRef(null);
  const themeRef = useRef(null);

  const meta = useMemo(() => headerMetaForPath(location.pathname, ws?.mode), [location.pathname, ws?.mode]);

  const email = String(userProfile?.email || '').trim();

  const aiCredits = subscription?.ai_credits;
  const hasUnlimitedCredits =
    aiCredits?.total_available === 'illimitati' || aiCredits?.remaining === 'illimitati';
  const planRemaining = typeof aiCredits?.remaining === 'number' ? aiCredits.remaining : 0;
  const extraRemaining = typeof aiCredits?.extra === 'number' ? aiCredits.extra : 0;
  const totalAvailable =
    typeof aiCredits?.total_available === 'number'
      ? aiCredits.total_available
      : planRemaining + extraRemaining;
  const monthlyLimit = aiCredits?.monthly_limit;

  const planConfig = {
    free: { name: 'Free', color: 'bg-slate-100 text-slate-700 border-slate-200' },
    base: { name: 'Base', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    pro: { name: 'Pro', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    premium: { name: 'Premium', color: 'bg-amber-50 text-amber-800 border-amber-200' }
  };
  const currentPlan = planConfig[subscription?.plan] || planConfig.free;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Errore logout:', error);
    }
  };

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(event.target)) {
        setIsThemeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-background/60 backdrop-blur-xl border-b border-border/40 px-4 md:px-6 h-16 fixed top-0 inset-x-0 lg:left-64 z-40 transition-colors">
      <div className="h-full flex items-center justify-between gap-3">
        {/* Left: hamburger (mobile) + page title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden p-2 rounded-xl hover:bg-accent/50 transition-colors"
            aria-label="Apri menu"
          >
            <Menu className="w-5 h-5 text-foreground" />
          </button>

          <div className="min-w-0 flex-1">
            {meta.title ? (
              <div className="text-sm md:text-base font-semibold text-foreground truncate">{meta.title}</div>
            ) : null}
            {meta.subtitle ? (
              <div className="text-xs text-muted-foreground truncate hidden md:block">{meta.subtitle}</div>
            ) : null}
          </div>
        </div>

        {/* Right: theme + user */}
        <div className="flex items-center gap-2 min-w-0 justify-end shrink-0">
          {/* CTA social persistente (nascosta al lancio, vedi SOCIAL_FEATURES_ENABLED) */}
          {SOCIAL_FEATURES_ENABLED ? (
            <>
              <button
                type="button"
                onClick={() => openComposer('discussion')}
                className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-gradient-to-r from-sky-500/80 to-violet-500/80 text-white shadow-lg shadow-violet-500/20 hover:opacity-95 transition-all active:scale-95"
                aria-label="Condividi in Community"
                title="Condividi in Community"
              >
                <MessageSquarePlus className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => openComposer('discussion')}
                className="hidden md:inline-flex items-center gap-2 h-10 px-4 rounded-2xl bg-gradient-to-r from-sky-500/80 to-violet-500/80 text-white shadow-lg shadow-violet-500/20 hover:opacity-95 transition-all active:scale-95"
                aria-label="Condividi in Community"
                title="Condividi in Community"
              >
                <MessageSquarePlus className="w-4 h-4" />
                <span className="text-sm font-semibold">Condividi</span>
              </button>
            </>
          ) : null}

          <div className="relative" ref={themeRef}>
            <button
              type="button"
              onClick={() => setIsThemeOpen((v) => !v)}
              className="p-2 rounded-xl hover:bg-accent/50 transition-colors"
              aria-label="Tema"
              aria-haspopup="menu"
              aria-expanded={isThemeOpen}
              title={`Tema: ${theme === 'system' ? `Sistema (${resolvedTheme})` : theme}`}
            >
              <AnimatePresence mode="wait">
                {isDark ? (
                  <motion.div key="sun" initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -2 }} transition={{ duration: 0.15 }}>
                    <Sun className="w-5 h-5 text-foreground" />
                  </motion.div>
                ) : (
                  <motion.div key="moon" initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -2 }} transition={{ duration: 0.15 }}>
                    <Moon className="w-5 h-5 text-foreground" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            <AnimatePresence>
              {isThemeOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.16 }}
                  className="absolute right-0 mt-2 w-48 bg-background/80 backdrop-blur-2xl text-foreground rounded-2xl shadow-2xl border border-border/50 py-2 z-50 overflow-hidden ring-1 ring-black/5"
                  role="menu"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setTheme('light');
                      setIsThemeOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-accent/50 flex items-center gap-2 transition-colors ${theme === 'light' ? 'bg-accent/50 text-violet-600' : ''}`}
                    role="menuitem"
                  >
                    <Sun className="w-4 h-4" />
                    Chiaro
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTheme('dark');
                      setIsThemeOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-accent/50 flex items-center gap-2 transition-colors ${theme === 'dark' ? 'bg-accent/50 text-violet-500' : ''}`}
                    role="menuitem"
                  >
                    <Moon className="w-4 h-4" />
                    Scuro
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTheme('system');
                      setIsThemeOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-accent/50 flex items-center gap-2 transition-colors ${theme === 'system' ? 'bg-accent/50 text-violet-500' : ''}`}
                    role="menuitem"
                  >
                    <Laptop className="w-4 h-4" />
                    Sistema
                    <span className="ml-auto text-xs text-muted-foreground capitalize">{resolvedTheme}</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {userProfile && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="h-10 px-3 rounded-2xl bg-surface/50 border border-border/50 hover:bg-surface/80 transition-all focus-visible:ring-focus flex items-center gap-2 min-w-0 cursor-pointer text-foreground"
                aria-label="Menu utente"
              >
                <div
                  className="w-9 h-9 rounded-full overflow-hidden bg-brand/10 border border-brand/20 grid place-items-center text-xs font-semibold text-brand shrink-0"
                  aria-label={`Avatar profilo: ${displayName || 'Utente'}`}
                >
                  {photoURL ? (
                    <img src={photoURL} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    (initials || 'U')
                  )}
                </div>
                <div className="min-w-0 hidden sm:block">
                  <div className="block text-xs sm:text-sm font-semibold truncate max-w-[180px]">
                    {displayName || `${userProfile?.nome || ''} ${userProfile?.cognome || ''}`.trim() || 'Utente'}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 mt-2 w-72 bg-background/80 backdrop-blur-2xl text-foreground rounded-3xl shadow-2xl border border-border/50 py-2 z-50 overflow-hidden ring-1 ring-black/5"
                  >
                    {/* Header profilo */}
                    <div className="px-5 pt-4 pb-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-brand/10 border border-brand/20 grid place-items-center text-sm font-semibold text-brand shrink-0">
                          {photoURL ? (
                            <img src={photoURL} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            (initials || 'U')
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {displayName || `${userProfile?.nome || ''} ${userProfile?.cognome || ''}`.trim() || 'Utente'}
                          </div>
                          {email ? <div className="text-xs text-muted-foreground truncate">{email}</div> : null}
                        </div>
                      </div>
                    </div>

                    {/* Piano + crediti */}
                    <div className="px-5 pb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${currentPlan.color}`}>
                          <span>Piano</span>
                          <span className="font-bold uppercase tracking-wide">{currentPlan.name}</span>
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/40 px-3 py-1">
                          <span className="text-[11px] font-semibold">{hasUnlimitedCredits ? '∞' : totalAvailable}</span>
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">crediti</span>
                        </div>
                      </div>

                      {aiCredits && !hasUnlimitedCredits && monthlyLimit !== 'illimitati' && Number(monthlyLimit) > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                            <span>Consumo mensile</span>
                            <span>
                              {planRemaining} / {Number(monthlyLimit)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, (planRemaining / Number(monthlyLimit)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="my-1 h-px bg-border/50 mx-2" />

                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate(appPath('/settings'));
                      }}
                      className="w-full text-left px-5 py-3 text-sm hover:bg-accent/50 flex items-center gap-3 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-accent/50 flex items-center justify-center">
                        <Settings className="w-4 h-4 text-muted-foreground" />
                      </div>
                      Impostazioni account
                    </button>

                    <div className="my-1 h-px bg-border/50 mx-2" />

                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-5 py-3 text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-3 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                        <LogOut className="w-4 h-4" />
                      </div>
                      Esci
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;